import React from 'react';
import './LegalPages.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: November 5, 2025</p>
        
        <div className="legal-content">
          <section>
            <h2>1. Information We Collect</h2>
            <p>
              We collect minimal information to provide you with the best gaming experience. This includes:
            </p>
            <ul>
              <li>Player usernames for game leaderboards (Country Quiz, Secret Number Game, Gem Quest)</li>
              <li>Game scores, statistics, and progress data</li>
              <li>Tool usage data (Character Counter, Greeting Cards generator, Spin Wheels)</li>
              <li>Basic website usage analytics for improving our services</li>
              <li>Information you provide when contacting us or using feedback forms</li>
              <li>Browser information and IP address for security purposes</li>
            </ul>
          </section>

          <section>
            <h2>2. How We Use Your Information</h2>
            <ul>
              <li>Display leaderboards and game rankings across all competitive games</li>
              <li>Save and restore your game progress and preferences</li>
              <li>Improve game functionality, user experience, and website performance</li>
              <li>Provide personalized content and game recommendations</li>
              <li>Respond to your questions, support requests, and feedback</li>
              <li>Analyze usage patterns to enhance our services and develop new features</li>
              <li>Ensure website security and prevent fraud or abuse</li>
              <li>Comply with legal obligations and protect our rights</li>
            </ul>
          </section>

          <section>
            <h2>3. Information Sharing</h2>
            <p>
              We do not sell or share your personal information with third parties, except:
            </p>
            <ul>
              <li>When required by law</li>
              <li>To protect our website and users</li>
              <li>With service providers under strict confidentiality</li>
            </ul>
          </section>

          <section>
            <h2>4. Cookies and Local Storage</h2>
            <p>
              We use cookies and local storage to enhance your experience:
            </p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for basic website functionality</li>
              <li><strong>Performance Cookies:</strong> Help us understand how you use our games</li>
              <li><strong>Game Data Storage:</strong> Save your preferences, progress, and high scores locally</li>
              <li><strong>Analytics Cookies:</strong> Provide insights to improve our services</li>
            </ul>
            <p>
              You can control cookie settings in your browser, but some features may not work properly if disabled.
            </p>
          </section>

          <section>
            <h2>5. Third-Party Services and Advertising</h2>
            <p>
              <strong>Google AdSense:</strong> We use Google AdSense to display advertisements. Google may use cookies and similar technologies to serve personalized ads based on your interests and browsing history. 
            </p>
            <p>
              <strong>Google Analytics:</strong> We use Google Analytics to analyze website traffic and user behavior. This helps us improve our games and services.
            </p>
            <p>
              <strong>Your Advertising Choices:</strong> You can control personalized advertising by:
            </p>
            <ul>
              <li>Visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google Ad Settings</a></li>
              <li>Using the <a href="http://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance opt-out tool</a></li>
              <li>Adjusting your browser's privacy settings</li>
            </ul>
          </section>

          <section>
            <h2>6. Data Security and Retention</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. This includes:
            </p>
            <ul>
              <li>Secure data transmission using HTTPS encryption</li>
              <li>Regular security assessments and updates</li>
              <li>Limited access to personal data on a need-to-know basis</li>
              <li>Secure hosting infrastructure with regular backups</li>
            </ul>
            <p>
              We retain your information only as long as necessary for the purposes outlined in this policy or as required by law. Game scores and usernames may be retained indefinitely for leaderboard purposes unless you request deletion.
            </p>
          </section>

          <section>
            <h2>7. International Data Transfers</h2>
            <p>
              Your information may be processed and stored in countries other than your own. We ensure appropriate safeguards are in place for international data transfers in compliance with applicable data protection laws.
            </p>
          </section>

          <section>
            <h2>8. Children's Privacy (COPPA Compliance)</h2>
            <p>
              Our games are family-friendly and suitable for all ages. We do not knowingly collect personal information from children under 13 without verifiable parental consent. If you believe we have collected information from a child under 13, please contact us immediately, and we will take steps to remove such information.
            </p>
            <p>
              Parents and guardians can review, delete, or refuse further collection of their child's personal information by contacting us.
            </p>
          </section>

          <section>
            <h2>9. Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete information</li>
              <li><strong>Erasure:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Receive your data in a portable format</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
              <li><strong>Restriction:</strong> Request limitation of processing</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p>To exercise these rights, please contact us using the information provided below.</p>
          </section>

          <section>
            <h2>10. California Privacy Rights (CCPA)</h2>
            <p>
              California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to know, delete, and opt-out of the sale of personal information. We do not sell personal information to third parties.
            </p>
          </section>

          <section>
            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2>12. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <ul>
              <li>Through our <a href="/contact">Contact page</a></li>
              <li>Email: privacy@timepassgame.com</li>
              <li>For privacy-related inquiries, we will respond within 30 days</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
